import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, ClipboardList, FileText, Wrench, ShieldCheck } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useContent } from "@/context/ContentContext";
import { Reveal } from "@/components/Reveal";
import { SERVICES, IMAGES } from "@/data/siteData";
import { ProjectsHomeSection } from "@/components/ProjectsGallery";
import { FAQ } from "@/components/FAQ";

const BENEFITS = [
  { title: "Fleksibel kapacitet", text: "Ekstra hænder og kapacitet, når jeres projekt kræver det.", icon: Wrench },
  { title: "Tydelige aftaler", text: "Klart afgrænsede arbejdsområder aftalt på forhånd.", icon: ClipboardList },
  { title: "Fokus på kvalitet", text: "Ordentligt udført arbejde efter aftalte standarder.", icon: CheckCircle2 },
  { title: "Professionel dokumentation", text: "Korrekt dokumentation af det udførte arbejde.", icon: FileText },
];

const STEPS = [
  { n: "01", title: "Opgaven gennemgås", text: "Vi gennemgår opgavens omfang, placering og krav sammen med jer." },
  { n: "02", title: "Du modtager et tilbud", text: "I får et konkret tilbud med pris og tidsramme." },
  { n: "03", title: "Arbejdet planlægges og udføres", text: "Vi planlægger og udfører arbejdet efter aftalen." },
  { n: "04", title: "Projektet dokumenteres og afsluttes", text: "Det udførte arbejde dokumenteres og afsluttes korrekt." },
];

const Home = () => {
  const { content } = useContent();
  const hero = content?.hero || {};
  const seo = content?.seo || {};
  useSEO({ title: seo.home_title, description: seo.home_desc });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Aktiv byggeplads med kran i Nordeuropa" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gray-900/65" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-block text-[#FF5A00] font-semibold text-sm uppercase tracking-widest mb-5">Bygge- og entreprenørvirksomhed · Randers</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] text-balance">
              {hero.headline || "Professionelt bygge- og konstruktionsarbejde til danske virksomheder"}
            </h1>
            <p className="mt-6 text-lg text-gray-200 leading-relaxed max-w-2xl">
              {hero.subtext || "AA+PLUS MILJØSANERING udfører konstruktionsarbejde, nedrivning, renovering og praktiske byggepladsopgaver for entreprenører og andre erhvervskunder."}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/kontakt" data-testid="hero-cta-primary" className="inline-flex items-center justify-center gap-2 bg-[#FF5A00] text-white font-semibold px-7 py-4 rounded-sm hover:bg-[#E04F00] transition-colors">
                Indhent tilbud <ArrowRight size={18} />
              </Link>
              <Link to="/ydelser" data-testid="hero-cta-secondary" className="inline-flex items-center justify-center bg-white/10 text-white font-semibold px-7 py-4 rounded-sm border border-white/40 hover:bg-white/20 transition-colors backdrop-blur-sm">
                Se vores ydelser
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">En fleksibel samarbejdspartner på byggepladsen</h2>
            <p className="mt-6 text-lg text-gray-600 leading-relaxed">
              Vi hjælper danske bygge-, renoverings- og entreprenørvirksomheder med at gennemføre konkrete og klart definerede arbejdsopgaver. Vi kan indgå som underentreprenør eller selvstændig leverandør på både mindre og større projekter.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-16 lg:py-24 bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <span className="text-[#FF5A00] font-semibold text-sm uppercase tracking-widest">Ydelser</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">Det udfører vi for erhvervskunder</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.05}>
                <Link to={`/ydelser#${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block h-full bg-white border border-gray-200 p-8 rounded-sm hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-200">
                  <span className="inline-flex items-center justify-center w-12 h-12 bg-[#FF5A00]/10 rounded-sm mb-5">
                    <s.icon className="text-[#FF5A00]" size={24} />
                  </span>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{s.short}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#FF5A00] group-hover:gap-2 transition-all">Læs mere <ArrowRight size={16} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <span className="text-[#FF5A00] font-semibold text-sm uppercase tracking-widest">Derfor vælger virksomheder os</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">Pålidelig udførelse – fra aftale til dokumentation</h2>
              <p className="mt-5 text-lg text-gray-600 leading-relaxed">
                Vi lægger vægt på tydelig kommunikation, realistiske tidsplaner og korrekt dokumentation af det udførte arbejde.
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-5">
              {BENEFITS.map((b, i) => (
                <Reveal key={b.title} delay={i * 0.05}>
                  <div className="bg-gray-50 border border-gray-200 p-6 rounded-sm h-full">
                    <b.icon className="text-[#FF5A00] mb-4" size={26} />
                    <h3 className="font-bold text-gray-900 mb-2">{b.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{b.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 lg:py-28 bg-[#1F2937]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <span className="text-[#FF5A00] font-semibold text-sm uppercase tracking-widest">Sådan arbejder vi</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">En enkel og gennemskuelig proces</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.06}>
                <div className="bg-white/5 border border-white/10 p-7 rounded-sm h-full">
                  <div className="font-display font-extrabold text-3xl text-[#FF5A00] mb-4">{s.n}</div>
                  <h3 className="font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* B2B */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-sm border border-gray-200">
              <img src={IMAGES.scaffolding} alt="Nybyggeri under opførelse med stillads" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gray-900/75" />
              <div className="relative px-8 py-16 lg:px-16 lg:py-24 max-w-2xl">
                <ShieldCheck className="text-[#FF5A00] mb-5" size={40} />
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">Leder I efter en stabil underentreprenør?</h2>
                <p className="mt-5 text-lg text-gray-200 leading-relaxed">
                  Vi samarbejder med danske virksomheder, der har behov for ekstra kapacitet eller en ekstern samarbejdspartner til konkrete bygge-, konstruktions-, renoverings- eller nedrivningsopgaver.
                </p>
                <Link to="/samarbejde" data-testid="b2b-cta-btn" className="mt-8 inline-flex items-center gap-2 bg-[#FF5A00] text-white font-semibold px-7 py-4 rounded-sm hover:bg-[#E04F00] transition-colors">
                  Kontakt os om et samarbejde <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* REFERENCES (only shows once projects are published) */}
      <ProjectsHomeSection />

      {/* FAQ */}
      <FAQ />

      {/* FINAL CTA */}
      <section className="py-20 lg:py-28 bg-gray-50 border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">Lad os høre om jeres næste projekt</h2>
            <p className="mt-5 text-lg text-gray-600 leading-relaxed">
              Send os en kort beskrivelse af opgaven. Vi kontakter jer for at gennemgå projektet og mulighederne for et samarbejde.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt" data-testid="final-cta-primary" className="inline-flex items-center justify-center gap-2 bg-[#FF5A00] text-white font-semibold px-7 py-4 rounded-sm hover:bg-[#E04F00] transition-colors">Indhent tilbud <ArrowRight size={18} /></Link>
              <Link to="/kontakt" data-testid="final-cta-secondary" className="inline-flex items-center justify-center bg-white text-gray-900 font-semibold px-7 py-4 rounded-sm border-2 border-gray-900 hover:bg-gray-100 transition-colors">Kontakt os</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
